import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline';
import api from '../api';

const ReceiptForm = ({ expense, onClose, onSave }) => {
  const [receivableItems, setReceivableItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [existingReceiptId, setExistingReceiptId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch ordered items for this expense that can be received
  useEffect(() => {
    const fetchReceivableItems = async () => {
      try {
        const expenseId = expense.expense_id || expense.id;
        const response = await api.get('/api/goods-receipts/ordered-items', { params: { expenseId } });
        const rows = Array.isArray(response.data) ? response.data : [];
        
        // Show all items, but indicate which are fully received
        setReceivableItems(rows);
        
        // Check if there are any existing receipts for this expense
        const hasExistingReceipts = rows.some(item => item.total_received > 0);
        const allFullyReceived = rows.every(item => item.remaining_qty === 0);
        const availableItems = rows.filter(item => item.remaining_qty > 0);
        
        // Only show error for actual problems, not when items are fully received
        if (rows.length === 0) {
          setError('No ordered items found for this expense.');
        }
        // Remove the "All items have been fully received" messages - user doesn't want to see them
      } catch (err) {
        console.error('Error fetching receivable items:', err);
        const msg = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to load receivable items';
        setError(msg);
      }
    };

    fetchReceivableItems();
  }, [expense]);

  // Handle item selection change
  const handleItemChange = (e) => {
    const expenseLineId = e.target.value;
    setSelectedItem(expenseLineId);
    setSuccessMessage(''); // Clear success message when selecting new item
    setHasUnsavedChanges(!!expenseLineId); // Mark as having changes if item is selected
    
    // Find and set the selected item details from receivable items
    const item = receivableItems.find(item => (item.expense_line_id?.toString() === expenseLineId));
    setSelectedItemDetails(item || null);
    
    // Check if this item has existing receipts (edit mode)
    if (item && item.total_received > 0) {
      setEditMode(true);
      setQuantity(item.total_received.toString());
      setError('Editing existing receipt. You can update the quantity, add/change file, or modify notes.');
    } else {
      setEditMode(false);
      setQuantity('');
      setError('');
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  // Check if there are more items available to receive
  const hasMoreItemsToReceive = () => {
    return receivableItems.some(item => item.remaining_qty > 0 && item.expense_line_id?.toString() !== selectedItem);
  };

  const handleSave = async () => {
    const expenseId = expense.expense_id || expense.id;
    const lineItem = {
      expense_line_id: parseInt(selectedItem),
      expense_item_id: selectedItemDetails?.item_id || null,
      received_qty: parseFloat(quantity),
      notes: notes,
      is_edit: editMode // Add flag to indicate edit mode
    };
    
    // For Non-Inventory items, item_id might be null, which is okay
    // The backend should use expense_line_id to find the line
    
    const formData = new FormData();
    formData.append('lines', JSON.stringify([lineItem]));
    formData.append('receipt_note', notes);
    
    if (file) {
      formData.append('files[0]', file);
    }
    
    const response = await api.post('/api/goods-receipts/goods-receipts', formData, {
      params: { expenseId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    return response;
  };

  const handleSubmitAndClose = async () => {
    const expenseId = expense.expense_id || expense.id;
    await handleSave();
    await api.post('/api/goods-receipts/close', { expenseId }, { params: { expenseId } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    

    
    if (!selectedItem || !quantity) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate quantity and warn about over-receipt
    const qty = parseFloat(quantity);
    const maxQty = selectedItemDetails?.remaining_qty || 0;
    const orderedQty = selectedItemDetails?.ordered_qty || 0;
    
    if (qty <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }
    
    // Allow over-receipt but show warning
    if (qty > maxQty && maxQty > 0) {
      const confirmOverReceipt = window.confirm(
        `You are receiving ${qty} but only ${maxQty} is remaining (ordered: ${orderedQty}, already received: ${selectedItemDetails?.total_received || 0}).\n\nThis will result in over-receipt. Do you want to continue?`
      );
      if (!confirmOverReceipt) {
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      await handleSave();
      // DON'T call onSave() here - we want to keep the form open
      
      // Reset form for next entry
      setSelectedItem('');
      setSelectedItemDetails(null);
      setQuantity('');
      setNotes('');
      setFile(null);
      setEditMode(false);
      setHasUnsavedChanges(false); // Clear unsaved changes flag
      setSuccessMessage(editMode ? 'Receipt updated successfully! You can add another receipt or submit to lock all receipts.' : 'Receipt saved successfully! You can add another receipt or submit to lock all receipts.');
      
      // Clear the file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Refresh the receivable items to show updated quantities
      try {
        const expenseId = expense.expense_id || expense.id;
        const response = await api.get('/api/goods-receipts/ordered-items', { params: { expenseId } });
        const rows = Array.isArray(response.data) ? response.data : [];
        setReceivableItems(rows);
        
        // Also notify parent to update its local data (but don't close form)
        // We'll create a separate callback for this
        if (window.updateGoodsReceiptData) {
          window.updateGoodsReceiptData(expenseId);
        }
      } catch (refreshError) {
        console.error('Error refreshing items:', refreshError);
      }
    } catch (err) {
      console.error('❌ Error saving receipt:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      console.error('❌ Error message:', err.message);
      
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save receipt';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClick = async () => {
    if (!selectedItem || !quantity) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate quantity and warn about over-receipt
    const qty = parseFloat(quantity);
    const maxQty = selectedItemDetails?.remaining_qty || 0;
    const orderedQty = selectedItemDetails?.ordered_qty || 0;
    
    if (qty <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }
    
    // Allow over-receipt but show warning
    if (qty > maxQty && maxQty > 0) {
      const confirmOverReceipt = window.confirm(
        `You are receiving ${qty} but only ${maxQty} is remaining (ordered: ${orderedQty}, already received: ${selectedItemDetails?.total_received || 0}).\n\nThis will result in over-receipt. Do you want to continue?`
      );
      if (!confirmOverReceipt) {
        return;
      }
    }

    setLoading(true);
    setError('');
    try {
      await handleSubmitAndClose();
      const expenseId = expense.expense_id || expense.id;
      onSave(expenseId); // Pass expense ID to update local data
      onClose(); // Close the form
    } catch (err) {
      console.error('Error submitting receipt:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to submit receipt';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" style={{ background: 'var(--panel)', color: 'var(--text)' }}>
        <div className="flex justify-between items-center p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div>
            <h3 className="text-lg font-medium" style={{ color: 'var(--text)' }}>Add Receipt</h3>
            {receivableItems.length > 0 && (
              <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                {receivableItems.filter(item => item.remaining_qty > 0).length} items available to receive
                {receivableItems.filter(item => item.total_received > 0).length > 0 && 
                  ` • ${receivableItems.filter(item => item.total_received > 0).length} items already received`
                }
                {receivableItems.filter(item => item.remaining_qty === 0 && item.total_received > 0).length > 0 && 
                  ` • ${receivableItems.filter(item => item.remaining_qty === 0 && item.total_received > 0).length} fully received`
                }
              </p>
            )}
          </div>
          <button 
            onClick={onClose}
            className="hover:opacity-70" 
            style={{ color: 'var(--muted)' }}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 rounded-md text-sm" style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--error, #dc2626)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              {error}
            </div>
          )}
          
          {successMessage && (
            <div className="p-3 rounded-md text-sm" style={{ background: 'rgba(34, 197, 94, 0.1)', color: 'var(--success, #16a34a)', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
              {successMessage}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                Item <span style={{ color: 'var(--error, #dc2626)' }}>*</span>
              </label>
              <select
                value={selectedItem}
                onChange={handleItemChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={{ 
                  background: 'var(--panel)', 
                  color: 'var(--text)', 
                  borderColor: 'var(--border)' 
                }}
                required
              >
                <option value="">Select an ordered item</option>
                {receivableItems.map((item) => {
                  const isFullyReceived = item.remaining_qty === 0;
                  const hasPartialReceipt = item.total_received > 0 && item.remaining_qty > 0;
                  
                  return (
                    <option key={item.expense_line_id} value={item.expense_line_id} disabled={isFullyReceived}>
                      {item.display_name} - Ordered: {item.ordered_qty} {item.uom}
                      {item.total_received > 0 && ` (${item.total_received} received)`}
                      {isFullyReceived ? ' - ✓ FULLY RECEIVED' : ` - Available: ${item.remaining_qty}`}
                      {hasPartialReceipt ? ' - PARTIAL' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            {selectedItemDetails && (
              <div className="p-4 rounded-md" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                <h4 className="font-medium mb-3" style={{ color: 'var(--text)' }}>Item Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="block text-sm font-medium" style={{ color: 'var(--muted)' }}>SKU</label>
                    <div className="mt-1" style={{ color: 'var(--text)' }}>{selectedItemDetails.sku || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium" style={{ color: 'var(--muted)' }}>UOM</label>
                    <div className="mt-1" style={{ color: 'var(--text)' }}>{selectedItemDetails.uom || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium" style={{ color: 'var(--muted)' }}>Ordered Quantity</label>
                    <div className="mt-1" style={{ color: 'var(--text)' }}>{selectedItemDetails.ordered_qty}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium" style={{ color: 'var(--muted)' }}>Already Received</label>
                    <div className="mt-1" style={{ color: 'var(--text)' }}>{selectedItemDetails.total_received}</div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium" style={{ color: 'var(--muted)' }}>Available to Receive</label>
                    <div className="mt-1 font-semibold" style={{ color: 'var(--success, #16a34a)' }}>{selectedItemDetails.remaining_qty} {selectedItemDetails.uom}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
              {editMode ? 'Update Quantity' : 'Quantity Received'} <span style={{ color: 'var(--error, #dc2626)' }}>*</span>
              {editMode && <span className="text-xs ml-2" style={{ color: 'var(--primary, #3b82f6)' }}>(Currently: {selectedItemDetails?.total_received})</span>}
            </label>
            <input
              type="number"
              min="0.01"
              max={selectedItemDetails?.remaining_qty || undefined}
              step="0.01"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                setHasUnsavedChanges(true);
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ 
                background: 'var(--panel)', 
                color: 'var(--text)', 
                borderColor: 'var(--border)' 
              }}
              placeholder={selectedItemDetails ? `Max: ${selectedItemDetails.remaining_qty}` : '0'}
              required
            />
            {selectedItemDetails && (
              <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
                Maximum available: {selectedItemDetails.remaining_qty} {selectedItemDetails.uom}
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setHasUnsavedChanges(true);
              }}
              rows={3}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ 
                background: 'var(--panel)', 
                color: 'var(--text)', 
                borderColor: 'var(--border)' 
              }}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
              Receipt (Optional)
            </label>
            <div className="mt-1 flex items-center">
              <label className="cursor-pointer py-2 px-3 border rounded-md shadow-sm text-sm leading-4 font-medium hover:opacity-80" 
                     style={{ 
                       background: 'var(--panel)', 
                       color: 'var(--text)', 
                       borderColor: 'var(--border)' 
                     }}>
                <span>Upload File</span>
                <input
                  type="file"
                  className="sr-only"
                  onChange={handleFileChange}
                  accept="image/*,.pdf"
                />
              </label>
              {file && (
                <span className="ml-2 text-sm flex items-center" style={{ color: 'var(--muted)' }}>
                  <DocumentIcon className="h-4 w-4 mr-1" />
                  {file.name}
                </span>
              )}
            </div>
          </div>
          
          </form>
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border)', background: 'var(--panel-secondary, var(--panel))' }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded-md text-sm font-medium hover:opacity-80"
            style={{ 
              background: 'var(--panel)', 
              color: 'var(--text)', 
              borderColor: 'var(--border)' 
            }}
            disabled={loading}
          >
            Close
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium"
            style={{ 
              background: '#8c211e',
              color: '#ffffff',
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            disabled={loading}
          >
            {loading ? 'Saving...' : (editMode ? 'Update Receipt' : hasMoreItemsToReceive() ? 'Save & Add More' : 'Save Receipt')}
          </button>
          <button
            type="button"
            onClick={handleSubmitClick}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white"
            style={{ 
              background: '#5c1417',
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            disabled={loading}
          >
            {loading ? 'Submitting...' : (editMode ? 'Update & Lock' : 'Submit & Lock')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptForm;
